import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { Dialog } from '@xing-com/dialog';
import { ModalPage } from '@xing-com/modal-page';

import { useArticlesModuleContext } from '../../hooks/use-articles-module-context/use-articles-module-context';
import { LinkShareXingletLoader } from '../link-share-xinglet-loader/link-share-xinglet-loader';

import * as Styled from './link-share-modal-page.styles';

type LinkShareModalPageProps = {
  contextGlobalId?: string;
  linkShareGlobalId?: string;
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
};

export const LinkShareModalPage: React.FC<LinkShareModalPageProps> = ({
  contextGlobalId,
  linkShareGlobalId,
  setShowModal,
  showModal,
}) => {
  const { formatMessage } = useIntl();
  const { refetch } = useArticlesModuleContext();

  const [isFocusTrapChanged, setIsFocusTrapChanged] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [canRefetchArticlesList, setCanRefetchArticlesList] = useState(false);
  const hideModal = () => setShowModal(false);

  const onPublishCallback = useCallback(
    (canRefetchArticlesList: boolean) => {
      hideModal();

      if (canRefetchArticlesList) {
        setTimeout(() => {
          refetch();
        }, 0);
      }
    },
    [hideModal, refetch]
  );

  const closeModalHandler = useCallback(() => {
    if (isFormDirty) {
      setShowDialog(true);
    } else {
      hideModal();

      if (canRefetchArticlesList) refetch();
    }
  }, [isFormDirty, hideModal, canRefetchArticlesList]);

  return (
    <>
      <Styled.ModalWrapperGlobalStyles />
      <ModalPage
        closeButton={{ onClick: closeModalHandler }}
        isAnimated
        noGrid
        isShown={showModal}
        noMargin
        zIndex={250}
        className={Styled.MODAL_WRAPPER_CLASS_NAME}
        focusTrapProps={{
          active: !isFocusTrapChanged,
        }}
      >
        <Styled.LinkShareModalContainer data-testid="LINK-SHARE-MODAL-CONTAINER">
          <LinkShareXingletLoader
            name="@xing-com/crate-articles-link-share"
            contextGlobalId={contextGlobalId}
            linkShareGlobalId={linkShareGlobalId}
            onPublishCallback={onPublishCallback}
            setIsFormDirty={setIsFormDirty}
            setCanRefetchArticlesList={setCanRefetchArticlesList}
            onFocusTrapChange={(isDirty) => setIsFocusTrapChanged(isDirty)}
          />
        </Styled.LinkShareModalContainer>
      </ModalPage>

      <Dialog
        show={showDialog}
        text={formatMessage({
          id: 'EP_ARTICLES_LINK_SHARE_UNSAVED_DIALOG_MESSAGE',
        })}
        confirmLabel={formatMessage({
          id: 'EP_ARTICLES_LINK_SHARE_UNSAVED_DIALOG_LEAVE_BUTTON',
        })}
        cancelLabel={formatMessage({
          id: 'EP_ARTICLES_LINK_SHARE_UNSAVED_DIALOG_STAY_BUTTON',
        })}
        onCancel={() => {
          setShowDialog(false);
        }}
        onConfirm={() => {
          setShowDialog(false);
          setTimeout(() => {
            hideModal();
          }, 0);
        }}
      />
    </>
  );
};
